/**
 * The CLI's seam to the org-admin community routes (`/api/communities/*`), shared by
 * `wayai communities` and the `communities/` subtree's pull and push (gh #5003): the client,
 * which refreshes a browser login on a 401 (Decision 3), and the mapping of the routes'
 * refusals to messages that name the next step (Decision 2).
 */

import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiErrorCode } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { envTokenOverride } from '../lib/token-store.js';

export async function communitiesClient(): Promise<ApiClient> {
  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing (Decision 3).
  return new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
}

/**
 * Decision 2's mapping. The routes answer one 403 — `AUTHORIZATION_ERROR`, the interactive-session
 * gate refusing a scoped token — so exactly that becomes the `wayai login` message. A 404 is the
 * route's answer for an unknown id and for a caller who is not an owner or admin of the org alike,
 * and a 429 is the per-person budget every community route shares. Anything else is returned as is.
 */
export function communitiesFailure(err: unknown, notFound: string): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 403 && extractApiErrorCode(err) === 'AUTHORIZATION_ERROR') {
    const override = envTokenOverride()
      ? ' WAYAI_TOKEN is set in this shell and is sent instead of your sign-in: unset it first.'
      : '';
    return expected(
      'Communities are managed from a browser sign-in only, and this login is an API token. ' +
        `Run \`wayai login\` (without --token).${override}`,
    );
  }
  if (err.status === 404) return expected(notFound);
  if (err.status === 429) {
    const seconds = retryAfterSeconds(err);
    return expected(
      'Too many requests: every community request shares one per-person budget with your web console ' +
        `and your messages. Try again ${seconds === null ? 'in a minute' : `in ${seconds} s`}.`,
    );
  }
  return err;
}

/** A community request, its failure mapped by `communitiesFailure`. */
export async function callCommunities<T>(request: Promise<T>, notFound: string): Promise<T> {
  try {
    return await request;
  } catch (err) {
    throw communitiesFailure(err, notFound);
  }
}

/** The rate limiter's `details.retryAfter`, in seconds; null when the body carries none. */
export function retryAfterSeconds(err: ApiError): number | null {
  try {
    const value: unknown = (JSON.parse(err.body) as { details?: { retryAfter?: unknown } }).details?.retryAfter;
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.ceil(value) : null;
  } catch {
    return null;
  }
}

export const communityNotFound = (label: string) =>
  `Community ${label} not found, or you are not an owner or admin of its organization.`;

export const orgNotFound = (orgId: string) =>
  `Organization ${orgId} not found, or you are not an owner or admin of it.`;
